import { useState, useEffect, useCallback, createContext, useContext, useRef, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import { noteEntry } from "@/lib/entrySource";
import axios from "axios";
import i18n from "@/i18n";

// Apply RTL direction based on stored language on first load
const storedLang = localStorage.getItem("i18nextLng") || "";
if (storedLang === "ar") {
  document.documentElement.dir = "rtl";
  document.documentElement.lang = "ar";
} else if (storedLang) {
  document.documentElement.lang = storedLang;
}
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { ClerkProvider, useUser, useAuth as useClerkAuth } from "@clerk/clerk-react";
import { FeaturebaseProvider, useFeaturebase } from "featurebase-js/react";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { installGlobalCrashReporting } from "@/utils/crashReporter";
import { useApplyColorScheme } from "@/hooks/useColorScheme";
import { initAnalytics, analytics } from "@/lib/analytics";
import { isNativeShell } from "@/lib/nativeShell";
import { NativePurchaseNoticePage, NativePaywallRoute } from "@/components/NativePurchaseNotice";
import { lazyPage } from "@/lib/lazyPage";

// Catches crashes outside React's render cycle (event handlers, timers, async code) —
// ErrorBoundary below only sees render-time errors. Installed once at module load, same
// timing as the RTL-direction setup above.
installGlobalCrashReporting();

// PostHog — no-ops if REACT_APP_POSTHOG_KEY is unset. Privacy config lives in the lib.
initAnalytics();

// The workspace's General → Manage modules toggles decide server-side which surfaces
// (messenger/changelog/feedback) actually boot; nothing here forces one on.
// REACT_APP_FEATUREBASE_APP_ID is the real source of truth (set on Vercel) — the literal
// is only a local-dev fallback so this still works without pulling Vercel's env down.
const FEATUREBASE_APP_ID = process.env.REACT_APP_FEATUREBASE_APP_ID || "6aa0ef5d9fdd78cfef02790e";

// Pages
const WelcomePage = lazyPage(() => import("@/pages/WelcomePage"));
const StyleQuizPage = lazyPage(() => import("@/pages/StyleQuizPage"));
const FightStoryPage = lazyPage(() => import("@/pages/FightStoryPage"));
const CheckinPage = lazyPage(() => import("@/pages/CheckinPage"));
const SeasonWrappedPage = lazyPage(() => import("@/pages/SeasonWrappedPage"));
const GymPosterPage = lazyPage(() => import("@/pages/GymPosterPage"));
const ComboBuilderPage = lazyPage(() => import("@/pages/ComboBuilderPage"));
const OnboardingFlow = lazyPage(() => import("@/pages/OnboardingFlow"));
const PaywallPage = lazyPage(() => import("@/pages/PaywallPage"));
const PaymentSuccess = lazyPage(() => import("@/pages/PaymentSuccess"));
const HomePage = lazyPage(() => import("@/pages/HomePage"));
const TrainPage = lazyPage(() => import("@/pages/TrainPage"));
const ScorePage = lazyPage(() => import("@/pages/ScorePage"));
const SessionResultsPage = lazyPage(() => import("@/pages/SessionResultsPage"));
const TimerPage = lazyPage(() => import("@/pages/TimerPage"));
const LibraryPage = lazyPage(() => import("@/pages/LibraryPage"));
const SessionDetailPage = lazyPage(() => import("@/pages/SessionDetailPage"));
const ProfilePage = lazyPage(() => import("@/pages/ProfilePage"));
const BlockedAccountsPage = lazyPage(() => import("@/pages/BlockedAccountsPage"));
const SquadsPage = lazyPage(() => import("@/pages/SquadsPage"));
const SquadDetailPage = lazyPage(() => import("@/pages/SquadDetailPage"));
const SchoolLeaderboardPage = lazyPage(() => import("@/pages/SchoolLeaderboardPage"));
const LoginPage = lazyPage(() => import("@/pages/LoginPage"));
const LeaderboardPage = lazyPage(() => import("@/pages/LeaderboardPage"));
const FeedPage = lazyPage(() => import("@/pages/FeedPage"));
const CreatePostPage = lazyPage(() => import("@/pages/CreatePostPage"));
const GymsPage = lazyPage(() => import("@/pages/GymsPage"));
const GymDetailPage = lazyPage(() => import("@/pages/GymDetailPage"));
const CompetitionsPage = lazyPage(() => import("@/pages/CompetitionsPage"));
const CompetitionDetailPage = lazyPage(() => import("@/pages/CompetitionDetailPage"));
const PublicProfilePage = lazyPage(() => import("@/pages/PublicProfilePage"));
import FeedbackWidget from "@/components/FeedbackWidget";
const NotFoundPage = lazyPage(() => import("@/pages/NotFoundPage"));
import SubscriptionGate from "@/components/SubscriptionGate";
const LiveFeedPage = lazyPage(() => import("@/pages/LiveFeedPage"));
const StreamViewPage = lazyPage(() => import("@/pages/StreamViewPage"));
const GoLivePage = lazyPage(() => import("@/pages/GoLivePage"));
const AdvertisePage = lazyPage(() => import("@/pages/AdvertisePage"));
const PrivacyPolicyPage = lazyPage(() => import("@/pages/PrivacyPolicyPage"));
const TermsOfServicePage = lazyPage(() => import("@/pages/TermsOfServicePage"));
const EmoteStudioPage = lazyPage(() => import("@/pages/EmoteStudioPage"));
import { PushPrompt } from "@/components/PushPrompt";
const StreamerDashboardPage = lazyPage(() => import("@/pages/StreamerDashboardPage"));
const HighlightsPage = lazyPage(() => import("@/pages/HighlightsPage"));
const RateRoundPage = lazyPage(() => import("@/pages/RateRoundPage"));
const CalloutsPage = lazyPage(() => import("@/pages/CalloutsPage"));
const JoinSquadPage = lazyPage(() => import("@/pages/JoinSquadPage"));
const BillingPage = lazyPage(() => import("@/pages/BillingPage"));
import { takePendingInvite } from "@/lib/pendingInvite";
const TokensPage = lazyPage(() => import("@/pages/TokensPage"));
const DiscoverPage = lazyPage(() => import("@/pages/DiscoverPage"));
const FighterMarketPage = lazyPage(() => import("@/pages/FighterMarketPage"));
const OrdersPage = lazyPage(() => import("@/pages/OrdersPage"));
const ProCardPage = lazyPage(() => import("@/pages/ProCardPage"));
const ProApplyPage = lazyPage(() => import("@/pages/ProApplyPage"));
const ProDashboardPage = lazyPage(() => import("@/pages/ProDashboardPage"));
const TokenSuccessPage = lazyPage(() => import("@/pages/TokenSuccessPage"));
const TrendingClipsPage = lazyPage(() => import("@/pages/TrendingClipsPage"));
const FantasyPage = lazyPage(() => import("@/pages/FantasyPage"));
const FantasyPartnersPage = lazyPage(() => import("@/pages/FantasyPartnersPage"));
const FantasyAdminPage = lazyPage(() => import("@/pages/FantasyAdminPage"));
const FightCampPage = lazyPage(() => import("@/pages/FightCampPage"));
const FantasyResultsPage = lazyPage(() => import("@/pages/FantasyResultsPage"));

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;
const PUBLISHABLE_KEY = process.env.REACT_APP_CLERK_PUBLISHABLE_KEY;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
};

// Mobile app bridge: the native iOS shell (WKWebView wrapping this same site)
// has no Clerk web session, so it pushes a fresh Clerk token in periodically
// via window.__setMobileAuthToken. When present, it wins over the web Clerk
// SDK. See ios/VictoryAI/App/MainAppView.swift.
export const SIGNIN_ERROR_KEY = "victory_signin_error";
let setMobileToken = null;
window.__setMobileAuthToken = (token) => setMobileToken?.(token);
window.__clearMobileAuthToken = () => setMobileToken?.(null);

const AuthProvider = ({ children }) => {
  const { isSignedIn, isLoaded } = useUser();
  const { getToken, signOut } = useClerkAuth();
  const [user, setUser] = useState(null);
  const [mongoLoading, setMongoLoading] = useState(true);
  const [mobileToken, setMobileTokenState] = useState(null);
  const getTokenRef = useRef(getToken);

  useEffect(() => {
    getTokenRef.current = getToken;
  }, [getToken]);

  useEffect(() => {
    setMobileToken = setMobileTokenState;
    return () => { setMobileToken = null; };
  }, []);

  // Global axios interceptor — adds a Bearer token to every request.
  // Native mobile token (if the bridge above set one) takes priority.
  useEffect(() => {
    const id = axios.interceptors.request.use(async (config) => {
      try {
        const token = mobileToken || await getTokenRef.current();
        if (token) config.headers.Authorization = `Bearer ${token}`;
      } catch (e) {}
      return config;
    });
    return () => axios.interceptors.request.eject(id);
  }, [mobileToken]);

  const checkAuth = useCallback(async () => {
    if (!mobileToken && !isLoaded) return;
    if (!mobileToken && !isSignedIn) {
      setUser(null);
      setMongoLoading(false);
      return;
    }
    setMongoLoading(true);
    try {
      const token = mobileToken || await getToken();
      const res = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUser(res.data);
    } catch (e) {
      setUser(null);
      // Clerk says "signed in" but our server refused: without signing out, /login would send
      // them straight back to /home and round again, forever.
      const status = e?.response?.status;
      if (!mobileToken && (status === 401 || status === 503)) {
        try { sessionStorage.setItem(SIGNIN_ERROR_KEY, String(status)); } catch (err) {}
        try { await signOut(); } catch (err) {}
      }
    } finally {
      setMongoLoading(false);
    }
  }, [isSignedIn, isLoaded, getToken, mobileToken, signOut]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Refresh the user doc WITHOUT toggling global loading (so callers like the
  // payment-success pages aren't unmounted mid-flow).
  const refreshUser = useCallback(async () => {
    if (!mobileToken && !isSignedIn) return;
    try {
      const token = mobileToken || await getToken();
      const res = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUser(res.data);
    } catch (e) {}
  }, [isSignedIn, getToken, mobileToken]);

  const logout = useCallback(async () => {
    if (mobileToken) {
      // No web Clerk session to sign out of — just clear local state.
      setMobileTokenState(null);
      setUser(null);
      return;
    }
    await signOut();
    setUser(null);
  }, [signOut, mobileToken]);

  return (
    <AuthContext.Provider value={{
      user,
      setUser,
      loading: !mobileToken && (!isLoaded || mongoLoading),
      isAuthenticated: (mobileToken || (isLoaded && isSignedIn)) && !!user,
      login: () => {},
      logout,
      checkAuth,
      refreshUser,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

// Shown only while a page's code downloads, the first time that page is opened.
const PageLoader = () => (
  <div className="min-h-screen bg-victory-bg flex items-center justify-center" role="status" aria-label="Loading">
    <div className="w-12 h-12 border-4 border-victory-lime border-t-transparent rounded-full animate-spin"></div>
  </div>
);

const ProtectedRoute = ({ children, requireSubscription = false }) => {
  const { isAuthenticated, loading, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate("/login", { replace: true });
    } else if (!loading && isAuthenticated && requireSubscription) {
      if (!user?.has_subscription) {
        if (!user?.onboarding_completed) {
          navigate("/onboarding", { replace: true });
        } else {
          navigate("/paywall", { replace: true });
        }
      }
    }
  }, [loading, isAuthenticated, user, requireSubscription, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-victory-bg flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-victory-lime border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!isAuthenticated || (requireSubscription && !user?.has_subscription)) return null;
  return children;
};

const OnboardingRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !isAuthenticated) navigate("/login", { replace: true });
  }, [loading, isAuthenticated, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-victory-bg flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-victory-lime border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return isAuthenticated ? children : null;
};

const TrialExpirationBanner = () => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [trialInfo, setTrialInfo] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!isAuthenticated || !user?.has_subscription) return;
    const check = async () => {
      try {
        const res = await axios.get(`${API}/subscription/trial-status`);
        if (res.data.status === "trialing" && res.data.days_remaining <= 3) {
          setTrialInfo(res.data);
        }
      } catch (e) {}
    };
    check();
  }, [isAuthenticated, user]);

  if (!trialInfo || dismissed || isNativeShell()) return null;

  return (
    <div className="fixed top-0 left-0 right-0 z-50 bg-victory-orange text-white px-4 py-2 flex items-center justify-between text-sm">
      <span>
        Trial ends in <strong>{trialInfo.days_remaining} day{trialInfo.days_remaining !== 1 ? "s" : ""}</strong> — keep your progress going.
      </span>
      <div className="flex items-center gap-3">
        <button onClick={() => navigate("/paywall")} className="underline font-semibold whitespace-nowrap">Upgrade</button>
        <button onClick={() => setDismissed(true)} className="text-white/70 hover:text-white text-lg leading-none">×</button>
      </div>
    </div>
  );
};

const RootRedirect = () => {
  const { isAuthenticated, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen bg-victory-bg flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-victory-lime border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  return <Navigate to={isAuthenticated ? "/live" : "/welcome"} replace />;
};

// Finishes a /join link that was opened while signed out, once sign-up is done.
const PendingInvite = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  useEffect(() => {
    if (!isAuthenticated) return;
    const inviteId = takePendingInvite();
    if (!inviteId) return;
    axios.post(`${API}/invites/${inviteId}/accept`)
      .then(({ data }) => {
        analytics.capture("squad_invite_accepted", { already_member: data.already_member, after_signup: true });
        toast.success(`You're in ${data.name}`, { action: { label: "View", onClick: () => navigate(`/squads/${data.squad_id}`) } });
      })
      .catch((err) => toast.error(err?.response?.data?.detail || "Couldn't join that squad"));
  }, [isAuthenticated, navigate]);
  return null;
};

const AppRouter = () => {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const cleaned = noteEntry(location.search);
    if (cleaned !== null) navigate(`${location.pathname}${cleaned}${location.hash}`, { replace: true });
  }, [location.pathname, location.search, location.hash, navigate]);

  // The service worker hands us push links when it can't navigate an already-open tab itself.
  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;
    const onMessage = (e) => {
      const url = e.data?.type === "navigate" && typeof e.data.url === "string" ? e.data.url : null;
      if (url && url.startsWith("/")) navigate(url);
    };
    navigator.serviceWorker.addEventListener("message", onMessage);
    return () => navigator.serviceWorker.removeEventListener("message", onMessage);
  }, [navigate]);

  // Global 402 handler — fires when free-tier AI quota is exceeded mid-session
  useEffect(() => {
    const id = axios.interceptors.response.use(
      (r) => r,
      (err) => {
        if (err?.response?.status === 402 && err?.response?.data?.detail === "ai_quota_exceeded") {
          toast("Monthly AI limit reached", {
            description: "You've used your 10,000 free AI credits. Upgrade to Pro for unlimited access.",
            action: { label: "Upgrade", onClick: () => navigate("/paywall") },
            duration: 6000,
          });
        }
        return Promise.reject(err);
      }
    );
    return () => axios.interceptors.response.eject(id);
  }, [navigate]);

  // Fetch the most-visited pages in the background once the first screen is up, so tapping
  // them never waits on a download.
  useEffect(() => {
    const idle = window.requestIdleCallback || ((cb) => setTimeout(cb, 2000));
    const cancel = window.cancelIdleCallback || clearTimeout;
    const id = idle(() => [HomePage, LiveFeedPage, TrainPage, FeedPage].forEach((page) => page.preload()));
    return () => cancel(id);
  }, []);

  return (
    <>
      <TrialExpirationBanner />
      <PushPrompt />
      <PendingInvite />
      <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/welcome" element={<WelcomePage />} />
        <Route path="/style" element={<StyleQuizPage />} />
        <Route path="/login/*" element={<LoginPage />} />

        <Route path="/onboarding" element={<OnboardingRoute><OnboardingFlow /></OnboardingRoute>} />
        <Route path="/paywall" element={isNativeShell() ? <NativePaywallRoute /> : <OnboardingRoute><PaywallPage /></OnboardingRoute>} />
        <Route path="/payment/success" element={<OnboardingRoute><PaymentSuccess /></OnboardingRoute>} />

        {/* Free for all authenticated users */}
        <Route path="/home" element={<ProtectedRoute><HomePage /></ProtectedRoute>} />
        <Route path="/timer" element={<ProtectedRoute><TimerPage /></ProtectedRoute>} />
        <Route path="/library" element={<ProtectedRoute><LibraryPage /></ProtectedRoute>} />
        <Route path="/sessions/:sessionId" element={<ProtectedRoute><SessionDetailPage /></ProtectedRoute>} />
        <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
        <Route path="/fight-story" element={<ProtectedRoute><FightStoryPage /></ProtectedRoute>} />
        <Route path="/combos" element={<ProtectedRoute><ComboBuilderPage /></ProtectedRoute>} />
        <Route path="/blocked-accounts" element={<ProtectedRoute><BlockedAccountsPage /></ProtectedRoute>} />
        <Route path="/squads" element={<ProtectedRoute><SquadsPage /></ProtectedRoute>} />
        <Route path="/squads/:squadId" element={<ProtectedRoute><SquadDetailPage /></ProtectedRoute>} />
        <Route path="/schools" element={<ProtectedRoute><SchoolLeaderboardPage /></ProtectedRoute>} />
        <Route path="/leaderboard" element={<ProtectedRoute><LeaderboardPage /></ProtectedRoute>} />

        {/* AI-heavy features — subscription required at point of use */}
        <Route path="/train" element={<ProtectedRoute><SubscriptionGate feature="AI training analysis"><TrainPage /></SubscriptionGate></ProtectedRoute>} />
        <Route path="/score" element={<ProtectedRoute><SubscriptionGate feature="AI session scoring"><ScorePage /></SubscriptionGate></ProtectedRoute>} />
        <Route path="/score/results" element={<ProtectedRoute><SubscriptionGate feature="AI session results"><SessionResultsPage /></SubscriptionGate></ProtectedRoute>} />
        {/* Same page, reached with a real session_id in the URL so a reload/reopen
            can refetch instead of silently bouncing home (Tesler's Law: the results
            screen only worked for the idealized "never refresh, never get
            backgrounded" user before). */}
        <Route path="/score/results/:sessionId" element={<ProtectedRoute><SubscriptionGate feature="AI session results"><SessionResultsPage /></SubscriptionGate></ProtectedRoute>} />

        {/* Social / Network features — free tier */}
        <Route path="/feed" element={<ProtectedRoute><FeedPage /></ProtectedRoute>} />
        <Route path="/discover" element={<ProtectedRoute><DiscoverPage /></ProtectedRoute>} />
        <Route path="/shop" element={<ProtectedRoute><FighterMarketPage /></ProtectedRoute>} />
        <Route path="/orders" element={<ProtectedRoute><OrdersPage /></ProtectedRoute>} />
        <Route path="/pro/apply" element={<ProtectedRoute><ProApplyPage /></ProtectedRoute>} />
        <Route path="/pro/dashboard" element={<ProtectedRoute><ProDashboardPage /></ProtectedRoute>} />
        <Route path="/pro/:userId" element={<ProtectedRoute><ProCardPage /></ProtectedRoute>} />
        <Route path="/post/create" element={<ProtectedRoute><CreatePostPage /></ProtectedRoute>} />
        <Route path="/gyms" element={<ProtectedRoute><GymsPage /></ProtectedRoute>} />
        <Route path="/gyms/:gymId" element={<ProtectedRoute><GymDetailPage /></ProtectedRoute>} />
        <Route path="/gyms/:gymId/poster" element={<ProtectedRoute><GymPosterPage /></ProtectedRoute>} />
        <Route path="/checkin/:code" element={<ProtectedRoute><CheckinPage /></ProtectedRoute>} />
        <Route path="/wrapped" element={<ProtectedRoute><SeasonWrappedPage /></ProtectedRoute>} />
        <Route path="/compete" element={<ProtectedRoute><CompetitionsPage /></ProtectedRoute>} />
        <Route path="/compete/:compId" element={<ProtectedRoute><CompetitionDetailPage /></ProtectedRoute>} />
        <Route path="/profile/:userId" element={<ProtectedRoute><PublicProfilePage /></ProtectedRoute>} />

        {/* Live Streaming */}
        <Route path="/live" element={<ProtectedRoute><LiveFeedPage /></ProtectedRoute>} />
        <Route path="/stream/:streamId" element={<ProtectedRoute><StreamViewPage /></ProtectedRoute>} />
        <Route path="/go-live" element={<ProtectedRoute><GoLivePage /></ProtectedRoute>} />
        <Route path="/emotes" element={<ProtectedRoute><EmoteStudioPage /></ProtectedRoute>} />
        <Route path="/dashboard" element={<ProtectedRoute><StreamerDashboardPage /></ProtectedRoute>} />
        <Route path="/highlights" element={<ProtectedRoute><HighlightsPage /></ProtectedRoute>} />
        <Route path="/rate/:highlightId" element={<ProtectedRoute><RateRoundPage /></ProtectedRoute>} />
        <Route path="/callouts" element={<ProtectedRoute><CalloutsPage /></ProtectedRoute>} />
        <Route path="/join/:inviteId" element={<JoinSquadPage />} />
        <Route path="/billing" element={<ProtectedRoute><BillingPage /></ProtectedRoute>} />
        <Route path="/tokens" element={<ProtectedRoute>{isNativeShell() ? <NativePurchaseNoticePage /> : <TokensPage />}</ProtectedRoute>} />
        <Route path="/tokens/success" element={<ProtectedRoute><TokenSuccessPage /></ProtectedRoute>} />
        <Route path="/clips" element={<ProtectedRoute><TrendingClipsPage /></ProtectedRoute>} />
        <Route path="/clip/:postId" element={<ProtectedRoute><TrendingClipsPage /></ProtectedRoute>} />
        <Route path="/fantasy" element={<ProtectedRoute><FantasyPage /></ProtectedRoute>} />
        <Route path="/fantasy/admin" element={<ProtectedRoute><FantasyAdminPage /></ProtectedRoute>} />
        <Route path="/fantasy/partners" element={<FantasyPartnersPage />} />
        <Route path="/fantasy/results/:cardId" element={<FantasyResultsPage />} />
        <Route path="/camp" element={<ProtectedRoute><FightCampPage /></ProtectedRoute>} />

        {/* Public — no auth required */}
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/terms" element={<TermsOfServicePage />} />

        {/* Advertiser onboarding — public, no auth required */}
        <Route path="/advertise" element={isNativeShell() ? <NativePurchaseNoticePage /> : <AdvertisePage />} />
        <Route path="/advertise/success" element={<AdvertisePage success />} />

        <Route path="/" element={<RootRedirect />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      </Suspense>
    </>
  );
};

// Sits inside AuthProvider (so it can read the real user) and wraps FeaturebaseProvider
// (so identity flows in as a prop) — Phase 3. FeaturebaseProvider stays mounted the whole
// time regardless of auth state (anonymous visitors on /welcome, /login etc. still get
// Outbound messages), it just re-identifies when featurebaseJwt changes rather than being
// unmounted/remounted on login or logout.
function FeaturebaseRoot({ children }) {
  const { user } = useAuth();
  return (
    // The default launcher is a fixed bottom-right bubble that sits on top of the bottom
    // nav's Profile tab; the messenger opens from FeedbackWidget instead.
    <FeaturebaseProvider appId={FEATUREBASE_APP_ID} featurebaseJwt={user?.featurebaseJwt} hideDefaultLauncher>
      <FeaturebaseLogoutSync />
      {children}
    </FeaturebaseProvider>
  );
}

// Phase 4 — explicit shutdown() on sign-out (not just letting featurebaseJwt go back to
// undefined), so the messenger/widget identity and unread count are actually torn down
// rather than just left stale. Reacts to isAuthenticated rather than hooking each logout()
// call site directly, so it also covers the native-mobile-bridge token-clear path in
// window.__clearMobileAuthToken, which never calls the context's logout() function at all.
function FeaturebaseLogoutSync() {
  const { isAuthenticated } = useAuth();
  const { shutdown } = useFeaturebase();
  const wasAuthenticated = useRef(false);
  useEffect(() => {
    if (wasAuthenticated.current && !isAuthenticated) shutdown();
    wasAuthenticated.current = isAuthenticated;
  }, [isAuthenticated, shutdown]);
  return null;
}

// PostHog: one manual $pageview per client-side route change (capture_pageview is
// off), plus identify on login / reset on logout. Only the stable app user_id is
// sent — never email or name.
function AnalyticsTracker() {
  const location = useLocation();
  const { user, isAuthenticated } = useAuth();
  const identified = useRef(false);

  useEffect(() => {
    analytics.pageview(location.pathname);
  }, [location.pathname]);

  useEffect(() => {
    if (isAuthenticated && user?.user_id && !identified.current) {
      analytics.identify(user.user_id, {
        plan: user.has_subscription ? "pro" : "free",
        onboarded: !!user.onboarding_completed,
      });
      identified.current = true;
    } else if (!isAuthenticated && identified.current) {
      analytics.reset();
      identified.current = false;
    }
  }, [isAuthenticated, user]);

  return null;
}

function App() {
  // Follows the phone's light/dark setting in real time. The colours themselves come from the
  // one theme file (src/theme/theme.js), so every screen updates at once.
  useApplyColorScheme();
  return (
    // Outermost on purpose — catches a crash even in ClerkProvider's own first render
    // (this app has hit exactly that locally: a missing key throws from inside
    // ClerkProviderBase before anything else mounts).
    <ErrorBoundary>
      <ClerkProvider publishableKey={PUBLISHABLE_KEY}>
        <div className="App min-h-screen bg-victory-bg">
          <BrowserRouter>
            <AuthProvider>
              <FeaturebaseRoot>
                <AnalyticsTracker />
                <AppRouter />
                <FeedbackWidget />
                <Toaster position="top-center" toastOptions={{ style: { background: "rgb(var(--victory-card))", border: "1px solid rgb(var(--victory-border))", color: "rgb(var(--victory-text))" } }} />
              </FeaturebaseRoot>
            </AuthProvider>
          </BrowserRouter>
        </div>
      </ClerkProvider>
    </ErrorBoundary>
  );
}

export default App;
